import type { CSSProperties } from "react";

/**
 * <img> with AVIF/WebP derivatives and a reserved box.
 *
 * Pairs with scripts/optimize-media.mjs, which writes `<name>-<width>.avif`
 * and `<name>-<width>.webp` next to each original. `src` stays the original,
 * so if a derivative is missing the browser simply falls back to it — nothing
 * breaks, it is just heavier.
 *
 * `width`/`height` are the *intrinsic* pixel dimensions of the source. They
 * are here to give the element an aspect ratio before the bytes arrive; the
 * rendered size still comes from CSS. Passing them is what keeps media from
 * shoving the page around as it loads.
 */
export default function ResponsiveImage({
  src,
  widths,
  sizes,
  alt,
  width,
  height,
  className,
  style,
  loading = "lazy",
  fetchPriority,
  decoding = "async",
  ...rest
}: {
  src: string;
  /** Derivative widths that exist on disk for this source. */
  widths: number[];
  /** CSS `sizes`; describes the rendered box so the browser picks correctly. */
  sizes: string;
  alt: string;
  width: number;
  height: number;
  className?: string;
  style?: CSSProperties;
  loading?: "lazy" | "eager";
  fetchPriority?: "high" | "low" | "auto";
  decoding?: "async" | "sync" | "auto";
} & Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src" | "sizes" | "width" | "height" | "loading">) {
  const dot = src.lastIndexOf(".");
  const stem = src.slice(0, dot);
  const srcSet = (ext: string) => widths.map((w) => `${stem}-${w}.${ext} ${w}w`).join(", ");

  return (
    // `display: contents` keeps <picture> out of the layout entirely, so the
    // surrounding flex/grid rules keep applying to the <img> as before.
    //
    // The `display: none` on each <source> is load-bearing, not tidiness:
    // `display: contents` promotes ALL of <picture>'s children to be children
    // of the grid/flex container, and <source> has no UA display:none, so each
    // image was silently claiming three grid cells instead of one. In the
    // "Side by side" 2-column grid that pushed the back render a full row
    // below the front one. Hiding the sources costs nothing — the browser's
    // picture resource selection ignores CSS entirely.
    <picture style={{ display: "contents" }}>
      <source type="image/avif" srcSet={srcSet("avif")} sizes={sizes} style={{ display: "none" }} />
      <source type="image/webp" srcSet={srcSet("webp")} sizes={sizes} style={{ display: "none" }} />
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading={loading}
        fetchPriority={fetchPriority}
        decoding={decoding}
        className={className}
        style={style}
        {...rest}
      />
    </picture>
  );
}
